import { RefObject, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Button, SearchInput, Title, VSpacing } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import numberFormatter from 'bloko/common/numberFormatter';
import { formatToReactComponent } from 'bloko/common/trl';

import { useSubmitDraftForm } from 'src/components/Search/Common/Filters/hooks/draft/useSubmitDraftForm';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { CurrentGeoFilter, setSearchGeoDrawerCurrentFilter } from 'src/models/search/common/geoDrawer';
import { ClusterKey } from 'src/types/search/common/clusters';
import { NON_BREAKING_SPACE } from 'src/utils/constants/symbols';

import AreaButton from 'src/components/Search/Resume/Filters/Geo/AreaButton';
import AreaSelector from 'src/components/Search/Resume/Filters/Geo/AreaSelector';
import Drawer from 'src/components/Search/Resume/Filters/Geo/Drawer';
import Filters from 'src/components/Search/Resume/Filters/Geo/Filters';
import { useGeoFiltersAnalytics } from 'src/components/Search/Resume/Filters/Geo/hooks/useAnalytics';
import { useCloseGeoDrawer } from 'src/components/Search/Resume/Filters/Geo/hooks/useCloseGeoDrawer';
import { useGeoFiltersMetroRedesignExp } from 'src/components/Search/Resume/Filters/Geo/hooks/useGeoFiltersRedesignExp';
import { useOpenGeoDrawer } from 'src/components/Search/Resume/Filters/Geo/hooks/useOpenGeoDrawer';
import { useResetButtonState } from 'src/components/Search/Resume/Filters/Geo/hooks/useResetButtonState';
import { useSearchAreaState } from 'src/components/Search/Resume/Filters/Geo/hooks/useSearchAreaState';

import styles from './styles.less';

const TrlKeys = {
    title: 'search.resume.filters.geo.title',
    applyButtonText: 'search.resume.filters.geo.apply.button',
    resetButtonText: 'search.resume.filters.geo.reset.button',
    submitWithCount: 'search.resume.filters.geo.submitButtonWithCount',
    areaSearchPlaceholder: 'search.resume.filters.geo.areaSearchPlaceholder',
};

const GeoFilters: TranslatedComponent = ({ trls }) => {
    const dispatch = useDispatch();
    const count = useSelector((state) => state.searchDraft.count);
    const isLoading = useSelector((state) => state.searchDraft.isLoading);
    const isVisible = useSelector((state) => state.searchGeoDrawer.isVisible);
    const currentGeoFilter = useSelector((state) => state.searchGeoDrawer.currentGeoFilter);
    const areaCluster = useSelector((state) => state.searchDraftClusters[ClusterKey.Area]);
    const metroCities = useSelector((state) => state.metroCities);

    const isGeoFiltersMetroRedesignExp = useGeoFiltersMetroRedesignExp();
    const [showResetButton, onResetClick] = useResetButtonState();
    const isOneAreaSelected = areaCluster?.selectedValues?.length === 1;
    const selectedAreaId = areaCluster?.groups[areaCluster?.selectedValues[0]]?.id || '';
    const selectedAreaQuery = areaCluster?.groups[areaCluster?.selectedValues[0]]?.title || '';
    const isSecondStep = currentGeoFilter !== CurrentGeoFilter.AREA;

    const hasMetroInCity = useMemo(() => metroCities?.includes(selectedAreaId), [metroCities, selectedAreaId]);
    const isMetroAvailable = isGeoFiltersMetroRedesignExp && hasMetroInCity;

    const citiesDistricts = useSelector((state) => state.citiesDistricts);
    const isDistrictsAvailable = selectedAreaId in citiesDistricts;

    const showContinueButton = isOneAreaSelected && !isSecondStep && (isMetroAvailable || isDistrictsAvailable);

    const openDraftForm = useOpenGeoDrawer();
    const closeDraftForm = useCloseGeoDrawer();
    const submitDraftForm = useSubmitDraftForm();
    const searchAreaState = useSearchAreaState();

    const { sendEventGeoDraftClose, sendEventGeoDraftSubmit, sendEventGeoDraftApplyOneArea } = useGeoFiltersAnalytics();

    const handleOpenDrawer = useCallback(() => {
        openDraftForm();
    }, [openDraftForm]);

    const handleClose = useCallback(() => {
        closeDraftForm();
        searchAreaState.handleQueryChange('');
        sendEventGeoDraftClose();
    }, [closeDraftForm, searchAreaState, sendEventGeoDraftClose]);

    const handleSubmit = useCallback(() => {
        submitDraftForm();
        searchAreaState.handleQueryChange('');
        sendEventGeoDraftSubmit();
    }, [submitDraftForm, searchAreaState, sendEventGeoDraftSubmit]);

    const setSecondStep = useCallback(() => {
        const activeFilter = isMetroAvailable ? CurrentGeoFilter.METRO : CurrentGeoFilter.DISTRICT;

        dispatch([setSearchGeoDrawerCurrentFilter(activeFilter)]);
        sendEventGeoDraftApplyOneArea(activeFilter);
    }, [isMetroAvailable, dispatch, sendEventGeoDraftApplyOneArea]);

    return (
        <>
            <AreaButton onClick={handleOpenDrawer} />
            <VSpacing default={24} />
            {/* TODO: Заменить на компонент из ДС в https://jira.hh.ru/browse/HH-248332 */}
            <Drawer
                visible={isVisible}
                onClose={handleClose}
                title={
                    <Title Element="h3" size="large">
                        {trls[TrlKeys.title]}
                    </Title>
                }
                options={
                    <SearchInput
                        value={isSecondStep ? selectedAreaQuery : searchAreaState.query}
                        onChange={searchAreaState.handleQueryChange}
                        onButtonClick={searchAreaState.handleClearButtonClick}
                        placeholder={trls[TrlKeys.areaSearchPlaceholder]}
                        clearable
                    />
                }
                footer={(ref) => (
                    <div className={styles.footer} ref={ref as RefObject<HTMLDivElement>}>
                        <div>
                            {showResetButton && (
                                <Button size="medium" mode="tertiary" style="accent" onClick={onResetClick}>
                                    {trls[TrlKeys.resetButtonText]}
                                </Button>
                            )}
                        </div>
                        {showContinueButton && (
                            <Button size="medium" mode="secondary" style="accent" onClick={setSecondStep}>
                                {trls[TrlKeys.applyButtonText]}
                            </Button>
                        )}
                        {!showContinueButton && (
                            <Button
                                size="medium"
                                mode="primary"
                                style="accent"
                                onClick={handleSubmit}
                                loading={isLoading}
                            >
                                {formatToReactComponent(trls[TrlKeys.submitWithCount], {
                                    '{0}': numberFormatter.format(String(count), {
                                        groupSeparator: NON_BREAKING_SPACE,
                                    }),
                                })}
                            </Button>
                        )}
                    </div>
                )}
            >
                {isSecondStep ? (
                    <Filters />
                ) : (
                    <AreaSelector
                        showSearchItems={searchAreaState.showSearchItems}
                        searchItems={searchAreaState.searchItems}
                    />
                )}
            </Drawer>
        </>
    );
};

export default translation(GeoFilters);
